"use client";
 
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
 
export default function PwaUpdater() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
 
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
 
    const handleControllerChange = () => {
      // Reload the page when the new service worker takes control
      window.location.reload();
    };
 
    navigator.serviceWorker.addEventListener("controllerchange", handleControllerChange);
 
    // Look for service worker registration
    navigator.serviceWorker.ready.then((reg) => {
      setRegistration(reg);
 
      // If a service worker is already waiting in the background, show the prompt
      if (reg.waiting) {
        setShowPrompt(true);
      }
 
      // Listen for new service workers being installed/waiting
      reg.addEventListener("updatefound", () => {
        const installingWorker = reg.installing;
        if (!installingWorker) return;
 
        installingWorker.addEventListener("statechange", () => {
          if (installingWorker.state === "installed" && navigator.serviceWorker.controller) {
            // New worker is installed and waiting (not the initial register)
            setShowPrompt(true);
          }
        });
      });
    });
 
    return () => {
      navigator.serviceWorker.removeEventListener("controllerchange", handleControllerChange);
    };
  }, []);
 
  const handleUpdate = () => {
    setShowPrompt(false);
    if (registration && registration.waiting) {
      // Tell the waiting worker to activate (skip waiting)
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    } else {
      // Fallback reload if registration/waiting worker isn't present
      window.location.reload();
    }
  };
 
  if (!showPrompt) return null;
 
  return (
    <div className="fixed bottom-24 left-4 right-4 md:left-auto md:right-8 md:max-w-sm z-[1000] bg-blue-900 text-white p-6 rounded-3xl shadow-2xl border border-cyan-500/20 flex flex-col gap-4 animate-in slide-in-from-bottom-8 duration-300">
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 shrink-0">
          <span className="material-symbols-outlined animate-pulse">system_update</span>
        </div>
        <div className="space-y-1">
          <h4 className="text-[10px] font-black uppercase tracking-widest text-cyan-300">System Update</h4>
          <p className="text-xs font-bold leading-normal">A new version of NautilusOne is available with performance and security upgrades.</p>
        </div>
      </div>
      <div className="flex gap-3">
        <Button variant="primary" size="sm"
          type="button"
          onClick={handleUpdate}
          className="flex-1 border flex items-center justify-center gap-1.5"
        >
          <span className="material-symbols-outlined text-xs">download_done</span>
          Update Now
        </Button>
        <Button variant="primary" size="md"
          type="button"
          onClick={() => setShowPrompt(false)}
          className="px-6 py-3 bg-white/10 hover:bg-white/20 text-slate-300 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all"
        >
          Later
        </Button>
      </div>
    </div>
  );
}
